// Monta a lista de conversas do chat. Há dois tipos:
// - projeto: cada projeto tem no máximo uma sala, então "conversa" = projeto; a
//   sala pode ainda não existir (`roomId: null`), e nesse caso é criada ao abrir.
// - direta: conversa privada entre duas pessoas, que sempre já tem sala (nasce
//   pela RPC get_or_create_direct_chat) e leva o nome da outra pessoa.

import type { Project, RoomSummary } from './types';
import { PREVIA_DE_MENSAGEM_DE_VOZ } from './attachmentMessage';

export type TipoDeConversa = 'projeto' | 'direta';

export interface ConversaItem {
  /** Identidade estável do item, única entre projetos e diretas. */
  chave: string;
  tipo: TipoDeConversa;
  /** null nas conversas diretas. */
  projectId: string | null;
  /** null quando a sala do projeto ainda não foi criada. */
  roomId: string | null;
  nome: string;
  /** Só nas diretas: foto e auth id da outra pessoa. */
  avatarUrl?: string | null;
  outroUsuarioId?: string | null;
  previa: string | null;
  ultimaMensagemEm: string | null;
  participantes: number;
  naoLidas: number;
}

export interface GruposDeConversas {
  naoLidas: ConversaItem[];
  diretas: ConversaItem[];
  projetos: ConversaItem[];
}

export const NOME_DE_CONVERSA_DIRETA = 'Conversa privada';

/** Chave do item de conversa de um projeto ou de uma sala direta. */
export const chaveDeProjeto = (projectId: string): string => `projeto:${projectId}`;
export const chaveDeDireta = (roomId: string): string => `direta:${roomId}`;

/** Última mensagem vista em tempo real numa sala, por roomId. */
export interface UltimaMensagem {
  conteudo: string;
  em: string;
}

interface EntradaDeConversas {
  projetos: Project[];
  resumoPorProjeto: Record<string, RoomSummary>;
  /** Resumo das salas diretas em que o usuário está. */
  salasDiretas?: RoomSummary[];
  /** Para saber quem é "a outra pessoa" numa conversa direta. */
  meuAuthId?: string | null;
  naoLidasPorSala: Record<string, number>;
  /**
   * O resumo das salas vem de uma RPC com cache em memória, resolvida uma vez
   * por organização — sem isto a prévia da lista congela no que existia quando
   * o chat abriu pela primeira vez.
   */
  ultimasPorSala?: Record<string, UltimaMensagem>;
  termo: string;
}

// Busca sem acento e sem caixa: quem digita "manutencao" espera achar
// "Manutenção Predial".
const normalizar = (texto: string): string =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const porRecencia = (a: ConversaItem, b: ConversaItem): number => {
  // Conversa sem nenhuma mensagem vai para o fim, ordenada por nome — não tem
  // data para comparar e não faz sentido disputar o topo com conversa ativa.
  if (a.ultimaMensagemEm && b.ultimaMensagemEm) {
    return b.ultimaMensagemEm.localeCompare(a.ultimaMensagemEm);
  }
  if (a.ultimaMensagemEm) return -1;
  if (b.ultimaMensagemEm) return 1;
  return a.nome.localeCompare(b.nome, 'pt-BR');
};

export function montarConversas({
  projetos,
  resumoPorProjeto,
  salasDiretas = [],
  meuAuthId,
  naoLidasPorSala,
  ultimasPorSala,
  termo,
}: EntradaDeConversas): GruposDeConversas {
  const busca = normalizar(termo);

  // Prévia, data e não lidas saem do mesmo jeito para os dois tipos.
  const atividade = (resumo: RoomSummary | undefined, roomId: string | null) => {
    let previa = resumo?.last_message_content ?? null;
    let ultimaMensagemEm = resumo?.last_message_created_at ?? null;

    // Só sobrepõe se for mesmo mais recente: o resumo pode ter sido
    // recarregado depois do registro em memória.
    const recebida = roomId ? ultimasPorSala?.[roomId] : undefined;
    if (recebida && (!ultimaMensagemEm || recebida.em > ultimaMensagemEm)) {
      previa = recebida.conteudo;
      ultimaMensagemEm = recebida.em;
    }

    return {
      // Texto vazio é mensagem de voz, que nasce sem texto de propósito.
      // Nulo continua sendo "nunca houve mensagem", e a linha some.
      previa: previa === '' ? PREVIA_DE_MENSAGEM_DE_VOZ : previa,
      ultimaMensagemEm,
      participantes: resumo?.participant_count ?? 0,
      naoLidas: (roomId && naoLidasPorSala[roomId]) || 0,
    };
  };

  const deProjetos = projetos.map<ConversaItem>(projeto => {
    const resumo = resumoPorProjeto[projeto.id];
    const roomId = resumo?.room_id ?? null;
    return {
      chave: chaveDeProjeto(projeto.id),
      tipo: 'projeto',
      projectId: projeto.id,
      roomId,
      nome: projeto.name,
      ...atividade(resumo, roomId),
    };
  });

  const diretas = salasDiretas.map<ConversaItem>(resumo => {
    const outro = resumo.participants.find(p => p.user_auth_id !== meuAuthId);
    return {
      chave: chaveDeDireta(resumo.room_id),
      tipo: 'direta',
      projectId: null,
      roomId: resumo.room_id,
      nome: outro?.name || NOME_DE_CONVERSA_DIRETA,
      avatarUrl: outro?.avatar_url ?? null,
      outroUsuarioId: outro?.user_auth_id ?? null,
      ...atividade(resumo, resumo.room_id),
    };
  });

  const filtrar = (itens: ConversaItem[]) =>
    itens.filter(item => !busca || normalizar(item.nome).includes(busca)).sort(porRecencia);

  const projetosFiltrados = filtrar(deProjetos);
  const diretasFiltradas = filtrar(diretas);

  return {
    naoLidas: [...diretasFiltradas, ...projetosFiltrados]
      .filter(item => item.naoLidas > 0)
      .sort(porRecencia),
    diretas: diretasFiltradas.filter(item => item.naoLidas === 0),
    projetos: projetosFiltrados.filter(item => item.naoLidas === 0),
  };
}

/**
 * Nome de exibição de uma sala direta para quem está nela: o nome da outra
 * pessoa. `membros` é a lista carregada da sala aberta.
 */
export function nomeDaConversaDireta(
  membros: Array<{ user_auth_id: string; user?: { name?: string | null } }>,
  meuAuthId: string | null | undefined
): string {
  const outro = membros.find(m => m.user_auth_id !== meuAuthId);
  return outro?.user?.name || NOME_DE_CONVERSA_DIRETA;
}
